import { Typography, useTheme } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRowParams,
} from '@spotgamma/x-data-grid-premium';
import {
  useEquityGridColumns,
  EquityTableType,
} from 'components/equityhub/EquityhubTable/useEquityScannerGridColumns';
import { Loader, UpsellModal } from 'components/shared';
import { EQUITYHUB_UPSELL } from 'config';
import useAuth from 'hooks/auth/useAuth';
import useEquities from 'hooks/equityhub/useEquities';
import usePrices from 'hooks/equityhub/usePrices';
import useLog from 'hooks/useLog';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CoreEquity,
  EquityFieldKey,
  LegacyEquity,
  ProductType,
  ScannerExclusiveColumnsCondensed,
  ScannerExclusiveColumnsCondensedWidths,
  ScannerID,
  SynthOIEquity,
} from 'types';
import { createDummyEquity, getEquitiesForScanner } from 'util/equityhub';
import {
  defaultGridTableSlots,
  getDefaultGridTableSlotProps,
  getDefaultGridTableStyles,
  updateSearch,
} from 'util/shared';

type ScannerRow = (LegacyEquity | SynthOIEquity) & {
  id: string;
  isWatchlisted: boolean;
};

const dummyEquities: ScannerRow[] = Array.from({ length: 4 }, (_, i) => ({
  id: `test-${i}`,
  isWatchlisted: false,
  ...(createDummyEquity(`DE-${i + 1}`) as unknown as LegacyEquity),
}));

interface ScannerTableProps {
  scanner: ScannerID;
  /**
   * Whether to fetch from the Synth-OI endpoint. Caller decides — usually
   * derived from `useSynthOi()` at the page level. The component itself
   * forces `false` when the scanner is legacy-only (Squeeze, wall-change,
   * etc.) since the Synth-OI endpoint has no data for those.
   */
  isSynthOI?: boolean;
  /**
   * Symbols the user has on any of their watchlists. Used to flag the
   * watchlist column. Passed in (vs read from Recoil) so this component
   * has no global-state dependencies and is trivially placeable in any
   * context — Canvas tiles, home quadrants, future surfaces.
   */
  watchlistedSyms: ReadonlySet<string>;
}

const ScannerTable = ({
  scanner,
  isSynthOI = false,
  watchlistedSyms,
}: ScannerTableProps) => {
  const theme = useTheme();
  const { logError } = useLog('ScannerTable');
  const [loading, setLoading] = useState<boolean>(false);
  const [errored, setErrored] = useState<boolean>(false);
  const [upsellOpen, setUpsellOpen] = useState<boolean>(false);
  const { hasAccessToProduct } = useAuth();
  const hasEHAccess = hasAccessToProduct(ProductType.EQUITYHUB);

  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { getEquityScanners } = useEquities();
  const { getPrices } = usePrices();

  // Per-instance rows so two Canvas tiles (e.g. Squeeze + VRP) can't
  // clobber each other's data.
  const [rows, setRows] = useState<ScannerRow[]>([]);

  const { columns } = useEquityGridColumns(
    theme,
    undefined,
    EquityTableType.StockScreener,
    isSynthOI,
    !hasEHAccess ? dummyEquities.map((e) => e.id) : undefined,
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrored(false);
      const rawEquityScanners = (await getEquityScanners(isSynthOI)) as Array<
        LegacyEquity | SynthOIEquity
      >;

      const bySym = new Map(rawEquityScanners.map((e) => [e.sym, e]));
      const prices = await getPrices(Array.from(bySym.keys()));

      const priced: Array<LegacyEquity | SynthOIEquity> = Array.from(
        bySym.values(),
      ).map((e) => {
        const price = prices.get(e.sym);
        return price != null ? { ...e, price } : e;
      });

      let nextRows: ScannerRow[] = priced.map((e) => ({
        ...e,
        id: e.sym,
        isWatchlisted: watchlistedSyms.has(e.sym),
      }));

      if (!hasEHAccess) {
        nextRows = [...nextRows, ...dummyEquities];
      }

      setRows(nextRows);
    } catch (err) {
      setErrored(true);
      logError(err, `${scanner} candidates`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSynthOI, scanner]);

  const setSelectedRow = (row: CoreEquity) => {
    if (!hasEHAccess) {
      return;
    }
    const newSym = row.sym;
    setSearchParams(updateSearch({ sym: newSym }));
    navigate(`/${ProductType.EQUITYHUB}?sym=${newSym}`);
  };

  const onRowClick = (params: GridRowParams) => {
    if (!hasEHAccess) {
      setUpsellOpen(true);
      return;
    }
    setSelectedRow(params.row as CoreEquity);
  };

  const filteredColumns = columns.filter((colDef: GridColDef) =>
    ScannerExclusiveColumnsCondensed[scanner].includes(
      colDef.field as EquityFieldKey,
    ),
  );

  const visibleRows = useMemo(
    () => getEquitiesForScanner(scanner, rows),
    [scanner, rows],
  );

  return (
    <Loader isLoading={loading}>
      {errored ? (
        <Typography
          sx={{
            fontSize: '16px',
            '&:hover': { cursor: 'pointer' },
          }}
          onClick={() => void fetchData()}
        >
          There was a network error. Click here to retry.
        </Typography>
      ) : (
        <>
          <DataGridPremium
            rows={visibleRows}
            columns={filteredColumns.map((colDef) => ({
              ...colDef,
              minWidth: 0,
              width:
                ScannerExclusiveColumnsCondensedWidths.get(
                  colDef.field as EquityFieldKey,
                ) ?? 95,
            }))}
            initialState={{
              pinnedColumns: { left: ['isWatchlisted', 'sym'] },
              sorting: {
                sortModel:
                  scanner === ScannerID.VOL_RISK_PREMIUM
                    ? [{ field: EquityFieldKey.ivRank, sort: 'desc' }]
                    : [],
              },
            }}
            rowHeight={47}
            onRowClick={onRowClick}
            disableMultipleRowSelection
            disableColumnPinning
            disableColumnSelector
            disableColumnMenu
            autoHeight
            hideFooter
            disableAggregation
            disableColumnReorder
            columnHeaderHeight={60}
            disableRowGrouping
            density="compact"
            slots={{ ...defaultGridTableSlots }}
            slotProps={{ ...getDefaultGridTableSlotProps(theme) }}
            sx={{
              ...getDefaultGridTableStyles(theme),
              maxWidth: 'fit-content',
            }}
          />
          <UpsellModal
            open={upsellOpen}
            setOpen={setUpsellOpen}
            title={EQUITYHUB_UPSELL.title}
            subtitle={EQUITYHUB_UPSELL.subtitle}
            items={EQUITYHUB_UPSELL.items}
          />
        </>
      )}
    </Loader>
  );
};

export default ScannerTable;
